import { Button, cn, Heading, Paragraph, Popup, SearchAutocomplete } from '@quickmd-llc/design-system'
// @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
import React, { useEffect, useMemo, useState } from 'react'
import '../styles.scss'
import { type IcdCodeStaffApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { IcdCodeChip } from './IcdCodeChip'
import { useGetIcdcodes } from 'api/generated/icd-code/icd-code'
import { useAutoUpdatingRef } from '@quickmd-llc/frontend-utils'
import { errorStatus, icd10Breadcrumb, useIcd10Diagnostics } from './icd10Diagnostics'

type Props = {
  onClose: () => void
  onSubmit: (codes: IcdCodeStaffApiOutContract[]) => void
  suggestedIcdCodes: IcdCodeStaffApiOutContract[]
  currentIcdCodes: IcdCodeStaffApiOutContract[]
}

type AutoItem = {
  key: string
  label: string
  value: string
  description: string
  data?: IcdCodeStaffApiOutContract
}

export const AddICD10CodesModal = ({ onClose, onSubmit, suggestedIcdCodes, currentIcdCodes }: Props) => {
  const [searchValue, setSearchValue] = useState<string>('')
  const [icd10SearchResults, setIcd10SearchResults] = useState<AutoItem[]>([])
  const [newCodes, setNewCodes] = useState<IcdCodeStaffApiOutContract[]>(currentIcdCodes)
  const getId = (c: IcdCodeStaffApiOutContract) => c.IcdCodeId ?? c.Code

  const suggestedCodes = useMemo<IcdCodeStaffApiOutContract[]>(() => {
    const selectedIds = new Set(newCodes.map(getId))

    return suggestedIcdCodes.filter((c) => !selectedIds.has(getId(c)))
  }, [suggestedIcdCodes, newCodes])

  const changes = useMemo(() => {
    const currentIds = new Set(currentIcdCodes.map(getId))
    const newIds = new Set(newCodes.map(getId))
    return {
      added: newCodes.filter((c) => !currentIds.has(getId(c))).length,
      removed: currentIcdCodes.filter((c) => !newIds.has(getId(c))).length,
    }
  }, [currentIcdCodes, newCodes])
  const hasChanges = changes.added > 0 || changes.removed > 0

  const handleSubmit = () => {
    icd10Breadcrumb('codes submitted', {
      codeCount: newCodes.length,
      ...changes,
      missingIdCount: newCodes.filter((c) => !c.IcdCodeId).length,
    })
    onSubmit(newCodes)
    setSearchValue('')
    setIcd10SearchResults([])
    onClose()
  }

  const {
    data: icdResults,
    isError: isSearchError,
    error: searchError,
  } = useGetIcdcodes<IcdCodeStaffApiOutContract[]>(searchValue ? { SearchValue: searchValue } : undefined, {
    query: { enabled: !!searchValue && searchValue.trim().length >= 2 },
  })
  const { onPointerDownCapture, noteSelected, reportOnce } = useIcd10Diagnostics(icd10SearchResults)
  // Read through refs so the effects below fire on open, on a response and on a failure — not per keystroke.
  const openCountsRef = useAutoUpdatingRef({
    currentCount: currentIcdCodes.length,
    suggestedCount: suggestedIcdCodes.length,
  })
  const queryLengthRef = useAutoUpdatingRef(searchValue.length)
  const searchErrorRef = useAutoUpdatingRef(searchError)

  useEffect(() => {
    icd10Breadcrumb('add ICD-10 modal opened', openCountsRef.current)
  }, [openCountsRef])

  useEffect(() => {
    if (icdResults) {
      icd10Breadcrumb('search results received', {
        resultCount: icdResults.length,
        queryLength: queryLengthRef.current,
      })
    }
  }, [icdResults, queryLengthRef])

  useEffect(() => {
    if (isSearchError) {
      reportOnce('search-failed', { status: errorStatus(searchErrorRef.current), queryLength: queryLengthRef.current })
    }
  }, [isSearchError, reportOnce, searchErrorRef, queryLengthRef])

  useEffect(() => {
    const q = searchValue.trim()
    if (!q) {
      setIcd10SearchResults([])
      return
    }
    const results = (icdResults || [])
      .filter((item) => !newCodes.find((c) => getId(c) === getId(item)))
      .map<AutoItem>((r) => ({
        // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
        key: r.IcdCodeId ?? r.Code,
        label: `${r.Code} ${r.Description}`,
        // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
        value: r.Code,
        // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
        description: r.Description,
        data: { ...r },
      }))
    setIcd10SearchResults(results)
  }, [searchValue, icdResults, newCodes])

  const onSelectIcd10Code = (item: AutoItem) => {
    const code = item.data
    if (!code) return
    noteSelected(!!code.IcdCodeId)
    setSearchValue('')
    setNewCodes((prev) => [...prev, code])
    setIcd10SearchResults([])
  }

  const resultIndexByKey = useMemo(
    () => new Map(icd10SearchResults.map((r, index) => [r.key, index])),
    [icd10SearchResults],
  )

  const renderIcd10Result = (item: AutoItem, handleSelect: () => void) => (
    <div
      role='presentation'
      data-icd-result-index={resultIndexByKey.get(item.key)}
      className='w-full cursor-pointer p-sp-1 text-left selection:bg-coastal-blue-10 hover:bg-coastal-blue-10'
      // Keep focus in the search input. Without this, pressing a row blurs the input and older
      // SearchAutocomplete versions close the list ~150ms later, so a slow click lands on nothing (QMD-4885).
      onMouseDown={(e) => e.preventDefault()}
      onClick={handleSelect}>
      <Paragraph className='whitespace-normal break-words'>
        <span className='font-semibold text-text-dark'>{item.data?.Code}</span>{' '}
        <span className='text-text-medium'>{item.data?.Description}</span>
      </Paragraph>
    </div>
  )
  const handleCodeToggle = (code: IcdCodeStaffApiOutContract) => {
    if (newCodes.find((c) => getId(c) === getId(code))) {
      setNewCodes((prev) => prev.filter((c) => getId(c) !== getId(code)))
    } else {
      setNewCodes((prev) => [...prev, code])
    }
  }
  return (
    <Popup
      onClose={onClose}
      isOpen
      showCloseIcon
      header={{
        content: <Heading level='h3'>Add ICD-10 code</Heading>,
      }}
      body={
        <div
          className={cn('flex h-max w-full flex-col gap-sp-2 rounded-lg', suggestedCodes.length === 0 && 'pb-[126px]')}>
          <Paragraph className='text-text-medium'>
            {suggestedCodes.length > 0 ?
              'Add ICD-10 codes from suggestions or by searching code or description.'
            : 'Search by ICD-10 code or description to add.'}
          </Paragraph>
          <div className='space-y-sp-1'>
            {newCodes.map((item) => (
              <IcdCodeChip key={item.IcdCodeId} code={item} variant='current' onClick={() => handleCodeToggle(item)} />
            ))}
          </div>
          {/* Observes presses on the results for the QMD-4885 diagnostics; it never changes them. */}
          <div onPointerDownCapture={onPointerDownCapture}>
            <SearchAutocomplete
              className='w-full [&>div]:[&>div]:!max-h-[110px] [&>div]:!p-0 [&>div]:[&>div]:p-sp-1'
              value={searchValue}
              onChange={setSearchValue}
              // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
              onResultSelect={onSelectIcd10Code}
              onSearch={setSearchValue}
              searchResults={icd10SearchResults}
              // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
              renderResult={renderIcd10Result}
            />
          </div>

          {suggestedCodes.length > 0 && (
            <div className='mb-sp-4'>
              <Paragraph className='mb-sp-2 font-semibold text-text-dark'>Suggested codes</Paragraph>
              <div className='space-y-sp-1'>
                {suggestedCodes.map((item) => (
                  <IcdCodeChip
                    key={item.IcdCodeId}
                    code={item}
                    variant='suggested'
                    onClick={() => handleCodeToggle(item)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      }
      footer={
        <div className='flex justify-end gap-sp-2'>
          <Button priority='primary' onClick={handleSubmit} disabled={!hasChanges}>
            Submit
          </Button>
        </div>
      }
    />
  )
}
